import EmberObject from '@ember/object';
import { readOnly } from '@ember/object/computed';
import Service, { inject as service } from '@ember/service';
import { BANNER_MESSAGE_TYPES } from '../states/application';
import { isValueInObject } from '../../utils/misc';
import { APPLICATION } from '../../models/application';
import { handleErrorResponseWithModal } from 'sh-www/utils/handles-errors';
import { showModal } from "sh-www/utils/modal-controls";
import { EMBER_ROUTE_NAMES } from '../../constants/routes';
import { v4 } from 'ember-uuid';

/** @type {Number} The default time we'll show a toast for. */
const DEFAULT_DURATION_TIME_MS = 4000;

export default Service.extend({
  /** Register services */
  activityFeedItemCommandService: service('commands/activity-feed-item-commands'),
  applicationCommandService : service('commands/application-commands'),
  applicationStateManager: service('state-managers/application'),
  assignmentCommandService : service('commands/assignment-commands'),
  elementCallback: service(),
  session: service(),
  errorTracker: service('error-tracker'),
  store: service(),
  router: service(),
  userCommandService: service('commands/user-commands'),
  notifications: service(),

  currentUser: readOnly('session.currentUser'),

  /**
   * The main state service for the state manager
   * @type {Service}
   */
  state: service('states/application'),

  /**
   * Show a toast in the application container.
   *
   * @param {String} toastMessage
   * @param {Object} opt_action       object defining title and action for a cta
   * @param {Number=} opt_duration    in ms or default will be used
   */
  addToast(toastMessage, opt_action, opt_duration) {
    const uniqueID = v4();
    const actionUniqueID = `toast-${uniqueID}-action`;
    const duration = opt_duration || DEFAULT_DURATION_TIME_MS;
    const actionHTML = opt_action ?
      `<button id="${actionUniqueID}" type="button" class="toast-notification__content__action-btn" onclick="window.elementCallbackService('${actionUniqueID}')">
        ${opt_action.text}
      </button>`
      : '';

    const toastHTML = `
      <div class="toast-notification__content">
        <span class="toast-notification__content__text">${toastMessage}</span>
        ${actionHTML}
      </div>
    `;

    const toastOptions = {
      htmlContent: true,
      cssClasses: 'toast-notification',
      autoClear: true,
      clearDuration: duration,
    }

    const notificationInstance = this.notifications.info(toastHTML, toastOptions);

    // callback for custom action
    if (opt_action) {
      this.elementCallback.addCallback(actionUniqueID, () => {
        // call custom action
        opt_action?.action();

        // close toast
        this.notifications.removeNotification(notificationInstance);
      });
    }
  },

  /**
   * Decline all pending applications.
   */
  declineAllPendingApplications() {
    let bannerMeta = this.get('state.bannerMessageMeta'),
      assignment = bannerMeta.get('assignment');

    this.set('state.isBannerLoading', true);

    this.get('assignmentCommandService').loadAssignment(
      assignment.get('id'),
      ['applications']
    ).then(assignment => {
      this.set('state.isBannerLoading', false);

      let filteredApps = assignment.get('applications').filter(application => {
          // only try to decline pending applications
          return application.get('status') === APPLICATION.STATUS.PENDING;
        }),
        applicationIds = filteredApps.map(app => app.get('id'));

      let modalModel = {
        title: `Decline pending proposals`,
        textAreaPlaceholder: `Optional: Leave a note.`,
        cancelText: 'Cancel',
        confirmText: 'Decline',
        secondaryMessage: `This decline message will be sent to ${applicationIds.length} freelancers`,
        onConfirmMessage: (message) => {
          this.set('state.isBannerLoading', true);
          this._declineApplications(applicationIds, message).then(() => {
            this.setNextRoute(EMBER_ROUTE_NAMES.POSTING__PENDING, null, assignment.get('idWithSlug'));
          }).finally(() => {
            this.set('state.isBannerLoading', false);
          });
        }
      };

      showModal(this, 'lsg/lsg-modal-text-editor', modalModel);
    }).catch(e => {
      handleErrorResponseWithModal(
        this,
        e,
        'There was an error declining the applications. Please refresh and try again.',
        'declineAllPendingApplications -- error loading assignment id: ' + assignment.get('id'),
      );
    }).finally(() => this.set('state.isBannerLoading', false));
  },

  /**
   * Dismiss the banner message for the application.
   */
  dismissBannerMessage() {
    let bannerMessageType = this.get('state.bannerMessageType');
    if (!bannerMessageType) {
      return;
    }

    this.set(`state.bannerDismissedSet.${bannerMessageType}`, true);
    this.set('state.bannerMessageType', null);
    this.set('state.bannerMessageMeta', EmberObject.create({}));
  },

  /**
   * Set the banner message for the application.
   *
   * @param {String} bannerMessageType              of type BANNER_MESSAGE_TYPES.*
   * @param {Boolean=} opt_resetDismissBannerFlag   set to true to always reset the banner isDismissed flag.
   * @param {Object=} opt_metaBag                   Optional object with extra params for extra data for a particular
   *                                                type of banner message.  e.g. dynamic link information
   */
  setBannerMessage(bannerMessageType, opt_resetDismissBannerFlag, opt_metaBag = {}) {
    if (!bannerMessageType) {
      this.dismissBannerMessage();
      return;
    }

    if (!isValueInObject(bannerMessageType, BANNER_MESSAGE_TYPES)) {
      this.get('errorTracker').error('setBannerMessage -- Unsupported banner message type ' + bannerMessageType, this);
      return;
    }

    if (opt_resetDismissBannerFlag) {
      this.set(`state.bannerDismissedSet.${bannerMessageType}`, false);
    }

    this.set('state.bannerMessageMeta', opt_metaBag);
    this.set('state.bannerMessageType', bannerMessageType);
  },

  /**
   * Set the next route for an Ember Transition.
   *
   * This is useful if you want to do a route transition from deep within a component hierarchy.
   *
   * @param {String} routeName      of type EMBER_ROUTE_NAMES.*
   * @param {Object|null=} opt_queryParams   Optional object with queryParams key to send to router.transitionTo
   * @param {*|null=} opt_model      Optional model to send to router.transitionTo
   * @returns {Transition}
   */
  setNextRoute(routeName, opt_queryParams = null, opt_model = null) {
    if (!routeName && !opt_queryParams) {
      throw new Error('ApplicationStateManager::setNextRoute - You must set at least routeName or opt_queryParams');
    }

    const router = this.get('router');

    if (!routeName) {
      return router.transitionTo(opt_queryParams);
    } else if (opt_model && opt_queryParams) {
      return router.transitionTo(routeName, opt_model, opt_queryParams);
    } else if (opt_queryParams) {
      return router.transitionTo(routeName, opt_queryParams);
    } else if (opt_model) {
      return router.transitionTo(routeName, opt_model);
    }

    return router.transitionTo(routeName);
  },

  /**
   * Update the list of recent activity feed items in our state.
   *
   * @param {Ember.A<ActivityFeedItem>} recentActivityFeedItems
   */
  updateRecentActivityFeedItems(recentActivityFeedItems) {
    this.set('state.mostRecentActivityFeedItems', recentActivityFeedItems);
  },

  /**
   * Update the most recent inbox threads. in our global app state.
   *
   * @param {Ember.A<Thread>} recentThreads
   */
  updateRecentInboxThreads(recentThreads) {
    this.set('state.mostRecentInboxThreads', recentThreads);
  },

  /**
   * Decline all the applications
   *
   * @param {String[]} applicationIds
   * @param {String} message
   * @private
   */
  _declineApplications(applicationIds, message) {
    return this.get('applicationCommandService').declineApplications(
      applicationIds,
      message
    ).then(() => {
      this.dismissBannerMessage();
    }).catch(e => {
      handleErrorResponseWithModal(
        this,
        e,
        'There was an error declining the applications. Please refresh and try again.',
        '_declineApplications -- error declining apps for currentUser id: ' + this.get('currentUser.id'),
      );
    });
  },
});
